<template>
  <Card cardClasses="shadow-sm border-0">
    <!-- Header Section -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-plus-circle me-2"></i>Add New Product
      </h4>
      <div>
        <button class="btn btn-outline-secondary" @click="$router.go(-1)">
          <i class="bi bi-arrow-left me-1"></i> Back
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <div class="product-form-content fade-in">
        <form @submit.prevent="">
          <!-- Basic Information Section -->
          <div class="border-0 shadow-sm mb-4">
            <div class="card-header bg-light py-3">
              <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>Basic Information</h5>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="form-group col-md-3">
                  <label for="code" class="form-label fw-bold">Product No:</label>
                  <input type="text" class="form-control" placeholder="Product No" v-model="product.code" readonly>
                </div>
                <div class="form-group col-md-3">
                  <label for="sku" class="form-label fw-bold">SKU:</label>
                  <div class="input-group">
                    <input type="text" class="form-control" placeholder="SKU" v-model="product.sku">
                    <button type="button" class="btn btn-outline-primary" @click="generateSku" data-toggle="tooltip" data-placement="top" title="Generate SKU">
                      <i class="bi bi-magic me-1"></i>
                    </button>
                  </div>
                </div>
                <div class="form-group col-md-6">
                  <label for="name" class="form-label fw-bold">Name:</label>
                  <input type="text" class="form-control" placeholder="Name" v-model="product.name">
                </div>
                <div class="form-group col-md-6">
                  <label for="validationTags" class="form-label fw-bold h5">Tags: <span class="text-black-50 fst-italic">(optional)</span></label>
                  <vue-tags-input
                      v-model="tag"
                      :add-on-key="[13, 32, ',']"
                      :tags="tags"
                      autocomplete-min-length="0"
                      style="background-color: #f2f2f2;
                        border: 2px solid #f2f2f2;
                        border-radius: 20px !important;
                        max-width: 100% !important;"
                      placeholder="search or type new tag"
                      :autocomplete-filter-duplicates="true"
                      :allow-edit-tags="true"
                      :autocomplete-always-open="showSuggestions"
                      :autocomplete-items="filteredItems"
                      @tags-changed="newTags => tags = newTags"
                  />
                  <span class="text-black-50 fst-italic" style="font-size: 15px">press enter, space or comma to add tag</span>
                </div>
                <div class="form-group col-md-6">
                  <label for="description" class="form-label fw-bold">Description: <span class="text-black-50 fst-italic small">(optional)</span></label>
                  <textarea class="form-control" placeholder="Description" v-model="product.description" rows="2"></textarea>
                </div>
              </div>
            </div>
          </div>

          <!-- Classification Section -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-light py-3">
              <h5 class="mb-0"><i class="bi bi-diagram-3 me-2"></i>Classification & Relationships</h5>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="form-group col-md-6">
                  <label for="category" class="form-label fw-bold">Category:</label>
                  <div class="input-group">
                    <select data="" type="text" class="form-select" v-model="product.category_id">
                      <option value="" disabled selected>Select category</option>
                      <option v-for="category in filteredCategories" :key="category.id" :value="category.id">{{category.name}}</option>
                    </select>
                    <button v-if="profile.role !== 'user'" type="button" class="btn btn-outline-success" @click="addCategory">
                      <i class="bi bi-plus-lg"></i> Add
                    </button>
                  </div>
                </div>
                <div class="form-group col-md-6">
                  <label for="brand" class="form-label fw-bold">Brand: <span class="text-muted fst-italic small fw-normal">(optional)</span></label>
                  <div class="input-group">
                    <select id="brand" class="form-select" v-model="product.brand_id">
                      <option :value="null">No brand</option>
                      <option v-for="brand in brands" :key="brand.id" :value="brand.id">{{ brand.name }}</option>
                    </select>
                    <button v-if="profile.role !== 'user'" type="button" class="btn btn-outline-success" @click="$router.push('/brands')">
                      <i class="bi bi-plus-lg"></i> Add
                    </button>
                  </div>
                </div>
                <div class="form-group col-md-6">
                  <label for="work_unit" class="form-label fw-bold">Unit:</label>
                  <div class="input-group">
                    <select data="" type="text" class="form-select" v-model="product.unit_id">
                      <option value="" disabled selected>Select unit</option>
                      <option v-for="unit in filteredUnits" :key="unit.id" :value="unit.id">{{unit.name}}</option>
                    </select>
                    <button v-if="profile.role !== 'user'" type="button" class="btn btn-outline-success" @click="addUnit">
                      <i class="bi bi-plus-lg"></i> Add
                    </button>
                  </div>
                </div>
                <div class="form-group col-md-6">
                  <label for="work_unit" class="form-label fw-bold">Tax Category:</label>
                  <select data="" type="text" class="form-select" v-model="product.tax_category_id">
                    <option value="" disabled selected>Select tax category</option>
                    <option v-for="category in filteredTaxCategories" :key="category.id" :value="category.id">{{category.rate}}% - {{category.description}}</option>
                  </select>
                </div>
                <div class="form-group col-md-6">
                  <label for="supplier" class="form-label fw-bold">Supplier:</label>
                  <div class="input-group">
                    <select data="" type="text" class="form-select" v-model="product.supplier_id">
                      <option value="" disabled selected>Select supplier</option>
                      <option v-for="supplier in filteredSuppliers" :key="supplier.id" :value="supplier.id">{{supplier.name}}</option>
                    </select>
                    <button v-if="profile.role !== 'user'" type="button" class="btn btn-outline-success" @click="addSupplier">
                      <i class="bi bi-plus-lg"></i> Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Inventory & Pricing Section -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-light py-3">
              <h5 class="mb-0"><i class="bi bi-currency-dollar me-2"></i>Inventory & Pricing</h5>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="form-group col-md-3">
                  <label for="stock" class="form-label fw-bold">Available Stock:</label>
                  <input type="number" class="form-control" placeholder="Available Stock" v-model="product.stock">
                </div>
                <div class="form-group col-md-3">
                  <label for="reorder" class="form-label fw-bold">Reorder Level:</label>
                  <input type="number" class="form-control" placeholder="Reorder Level" v-model="product.reorder">
                </div>
                <div class="form-group col-md-3">
                  <label for="buy" class="form-label fw-bold">Buying Price:</label>
                  <div class="input-group">
                    <span class="input-group-text"><i class="bi bi-cash"></i></span>
                    <input type="number" class="form-control" placeholder="Buying Price" v-model="product.buy">
                  </div>
                </div>
                <div class="form-group col-md-3">
                  <label for="sell" class="form-label fw-bold">Selling Price:</label>
                  <div class="input-group">
                    <span class="input-group-text"><i class="bi bi-tag"></i></span>
                    <input type="number" class="form-control" placeholder="Selling Price" v-model="product.sell">
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
        <!-- Action Buttons -->
        <div class="d-flex justify-content-center gap-3 mt-4 mb-3">
          <button class="btn btn-outline-secondary px-4" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Cancel
          </button>
          <button v-if="profile.role !== 'user'" type="submit" class="btn btn-primary px-4" @click="submitForm">
            <i class="bi bi-check-lg me-1"></i> Save Product
          </button>
        </div>
      </div>
    </div>
  </Card>
</template>

<script>
import productService from "@/api/productService";
import categoryService from "@/api/categoryService";
import brandService from "@/api/brandService";
import unitService from "@/api/unitService";
import supplierService from "@/api/supplierService";
import router from "@/router";
import {decrypt} from "@/utils/crypto";
import taxCategoryService from "@/api/taxCategoryService";
import loadingButton from "@/utils/loadingButton";
import VueTagsInput from '@sipec/vue3-tags-input';

export default {
  name: "AddProduct",
  components: {
    VueTagsInput,
  },
  data() {
    const organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    return {
      categories: [],
      brands: [],
      units: [],
      suppliers: [],
      taxCategories: [],
      product: {
        organization_id: organization.id,
        code: '',
        sku: '',
        name: '',
        reorder: '',
        stock: '',
        buy: '',
        sell: '',
        meta: '',
        tags: '',
        description: '',
        category_id: '',
        brand_id: null,
        supplier_id: '',
        unit_id: '',
        tax_category_id: '',
      },
      profile: {},
      organization: organization,
      nextNo: {},
      showSuggestions: false,
      tag: '',
      tags: [],
      suggestions: []
    }
  },
  methods: {
    submitForm: async function (e) {
      loadingButton(e)
      await productService.createProduct(this.product)
      loadingButton(e, false)
    },
    generateSku: function () {
      // Get the last three digits of the organization ID
      const orgId = this.profile.organization_id || '43';
      const lastThreeDigits = orgId.toString().slice(-3);

      // Convert the hexadecimal to decimal
      const decimalValue = parseInt(lastThreeDigits, 16);

      // Generate a time-based number
      const timestamp = new Date().getTime();

      // Concatenate the decimal value at the beginning of the timestamp
      this.product.sku = decimalValue.toString() + timestamp.toString().slice(-7);
    },
    addCategory: function () {
      router.push({
        path: 'categories',
        query: { add: 'true' }
      })
    },
    addSupplier: function () {
      router.push({
        path: 'add-supplier',
      })
    },
    addUnit: function () {
      router.push({
        path: 'units',
        query: { add: 'true' }
      })
    },
  },

  created: async function () {
    try {
      this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
      this.nextNo = await productService.getProductNextNo()
      this.product.code = this.nextNo.next
      this.suggestions = this.nextNo.tags
      
      const categoriesResponse = await categoryService.getCategories({ per_page: 500 })
      this.categories = categoriesResponse?.data || []

      const brandsResponse = await brandService.getBrands({ all: 1 })
      this.brands = Array.isArray(brandsResponse) ? brandsResponse : []
      
      const suppliersResponse = await supplierService.getSuppliers({ per_page: 500 })
      this.suppliers = suppliersResponse?.data || []
      
      const taxCategoriesResponse = await taxCategoryService.getTaxCategories()
      this.taxCategories = taxCategoriesResponse || []
      
      const unitsResponse = await unitService.getUnits({ per_page: 500 })
      this.units = unitsResponse?.data || []
    } catch (error) {
      console.error('Error in created hook:', error)
    }
  },

  computed: {
    filteredItems() {
      return this.suggestions.filter(i => {
        return i.toLowerCase().indexOf(this.tag.toLowerCase()) !== -1;
      });
    },
    filteredCategories() {
      return this.categories.filter(c => c !== null && c !== undefined);
    },
    filteredUnits() {
      return this.units.filter(u => u !== null && u !== undefined);
    },
    filteredSuppliers() {
      return this.suppliers.filter(s => s !== null && s !== undefined);
    },
    filteredTaxCategories() {
      return this.taxCategories.filter(t => t !== null && t !== undefined);
    },
  },

  watch: {
    'product.stock': function () {
      let reorder = this.nextNo.reorder ?? 50
      this.product.reorder = Math.ceil(this.product.stock * (reorder/100))
    },
    'product.category_id': function (newCategoryId) {
      if (newCategoryId && this.product.buy) {
        const selectedCategory = this.categories.find(c => c.id === newCategoryId)
        if (selectedCategory && selectedCategory.sale_margin_percentage) {
          const margin = selectedCategory.sale_margin_percentage
          this.product.sell = Math.ceil(((this.product.buy * (margin/100)) + this.product.buy))
        }
      }
    },
    'product.buy': function () {
      if (this.product.category_id) {
        const selectedCategory = this.categories.find(c => c.id === this.product.category_id)
        if (selectedCategory && selectedCategory.sale_margin_percentage) {
          const margin = selectedCategory.sale_margin_percentage
          this.product.sell = Math.ceil(((this.product.buy * (margin/100)) + this.product.buy))
          return
        }
      }
      let margin = this.nextNo.margin ?? 20
      this.product.sell = Math.ceil(((this.product.buy * (margin/100)) + this.product.buy))
    },
    tags: function () {
      this.product.tags = this.tags.map(tag => tag.text).join(',')
    }
  }

}
</script>

<style scoped>
/* Form styles */
.product-form-content {
  padding: 0.5rem;
}

.form-label {
  font-size: 0.9rem;
  margin-bottom: 0.3rem;
}

.card-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.card {
  transition: all 0.3s ease;
  overflow: hidden;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.05) !important;
}

/* Animation for content loading */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  .input-group-text {
    padding: 0.25rem 0.5rem;
  }
}
</style>
